import { Injectable, inject } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { CollectionRequestBuilder } from '@web/common';
import { Guid, once } from '@web/utils';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { AdminDictionaryCountryCollectionItem } from '../models/admin-dictionary-country-collection-item';
import { AdminDictionaryCountryCollectionFilter } from '../models/admin-dictionary-country-collection-filter';
import { AdminDictionaryCountryApiService } from '../api/services/admin-dictionary-country-api.service';
import { AdminDictionaryCountryDetailsForm } from '../models/admin-dictionary-country-details-form';
import { AdminDictionaryCountryUpdateRequest } from '../api/requests/admin-dictionary-country-update.request';
import { AdminDictionaryCountryCreateRequest } from '../api/requests/admin–dictionary-country-create.request';

@Injectable({
  providedIn: 'root',
})
export class AdminDictionaryCountryDataSourceService {
  /* prettier-ignore */
  private readonly adminDictionaryApi = inject(AdminDictionaryCountryApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);

  /* prettier-ignore */
  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminDictionaryCountryCollectionFilter>,
  ): Observable<TableDataPayload<AdminDictionaryCountryCollectionItem>> {
    return this.adminDictionaryApi
      .fetchCollection(
        {
          request: this.collectionRequestBuilder
            .page(lazyLoad.page)
            .pageSize(lazyLoad.rows)
            .sorts((builder) =>
              builder.fromTableSort(lazyLoad.sort).buildAsArray(),
            )
            .filters((builder) =>
              builder
                .fromTableFilter(lazyLoad.filter)
                .appendText((builder) => builder.caseInsensitiveLikeFor('label').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('isoCode').build())
                .appendDate((builder) => builder.rangeFor('createdAt').build())
                .buildAsArray(),
            )
            .build()
        },
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  new AdminDictionaryCountryCollectionItem({
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                    createdAt: item.createdAt,
                    isoCode: item.isoCode,
                    id: item.id,
                    label: item.label,
                    position: item.position,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchDetailsFormById(
    id: Guid,
  ): Observable<AdminDictionaryCountryDetailsForm> {
    return this.adminDictionaryApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new AdminDictionaryCountryDetailsForm({
            label: response.label,
            position: response.position,
            isoCode: response.isoCode,
          }),
      ),
    );
  }

  public confirmAndRemoveById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć wybrany element?')
          .accept(() =>
            this.adminDictionaryApi
              .remove(id)
              .pipe(
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrany element został pomyślnie usunięty.')
                      .build(),
                  ),
                ),
                once(),
              )
              .subscribe(subscriber),
          )
          .build(),
      );
    });
  }

  public remove(id: Guid): Observable<void> {
    return this.adminDictionaryApi.remove(id);
  }

  public updateByFormData(
    id: Guid,
    data: AdminDictionaryCountryDetailsForm,
  ): Observable<void> {
    const request = new AdminDictionaryCountryUpdateRequest({
      label: data.label,
      position: data.position,
      isoCode: data.isoCode,
    });

    return this.adminDictionaryApi.update(id, request);
  }

  public createByFormData(
    data: AdminDictionaryCountryDetailsForm,
  ): Observable<void> {
    const request = new AdminDictionaryCountryCreateRequest({
      label: data.label,
      position: data.position,
      isoCode: data.isoCode,
    });

    return this.adminDictionaryApi.create(request);
  }
}
